import React from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Sparkles, Target, TrendingUp, Heart, Users, Download, Share2, RotateCcw, Eye, X, Lightbulb, Film, User, Globe, Zap, BookOpen, Dumbbell, Mountain, Music, Shield, Upload, Plus, FileText, ChevronRight, Clock, Twitter, Linkedin, Mail, Copy, Check } from 'lucide-react';
import { INDUSTRIES, getOptionsForIndustry } from "./IndustryData";
import { getCareersForProfile } from "./CareerMapping";
import { getTileDescription } from "./TileDescriptions";
import UserInfoForm from './components/UserInfoForm';
import PaywallModal from './components/PaywallModal';
import PaymentSuccessScreen from './components/PaymentSuccessScreen';
import { saveUser, updateUserProfile, checkUserPremium, deleteUserAccount, verifyCheckout, syncIOSEntitlement } from './services/account';
import { redirectToPayment, checkPaymentStatus } from './services/stripe';
import { Capacitor } from '@capacitor/core';
import { initPayments, handlePurchase, checkPremiumStatus as checkIOSPremium, getIOSAppUserId } from './services/payment';
const { memo } = React;
// Analytics tracking
const trackEvent = (eventName, properties = {}) => {
  try {
    console.log(`?? Analytics: ${eventName}`, properties);
    // localStorage tracking for conversion funnel
    const events = JSON.parse(localStorage.getItem('miiq-analytics') || '[]');
    events.push({
      event: eventName,
      properties,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('miiq-analytics', JSON.stringify(events));
  } catch (e) {
    console.error('Analytics error:', e);
  }
};
const QUESTIONS = [
  { key: "experiences", title: "Experiences: What Do You Want to Experience?", icon: Target, color: "from-[#2E1A47] to-[#D4AF37]" },
  { key: "growth", title: "Growth: How Do You Want to Grow?", icon: TrendingUp, color: "from-blue-500 to-cyan-600" },
  { key: "contribution", title: "Contribution: What Do You Want to Contribute?", icon: Heart, color: "from-green-500 to-emerald-600" }
];

const PROFILES = {
  "The Visionary Creator":{ icon:Lightbulb, emoji:"🎨", description:"You are driven by innovation and bringing new ideas to life. Your focus on creative experiences, continuous growth, and inspiring others shows a pioneering spirit.", traits:["Innovative","Artistic","Inspiring","Forward-thinking"], strengths:"Your ability to envision and create new possibilities sets you apart. You naturally inspire others through your creative work.", growthAreas:"Balance visionary thinking with practical execution. Collaborate with detail-oriented partners.", careers:["Creative Director","Entrepreneur","Artist","Innovation Leader","Content Creator"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Authentic Performer":{ icon:Film, emoji:"🎭", description:"You thrive in the spotlight and express yourself through performance. Your desire for stage experiences and inspiring others reveals your natural charisma.", traits:["Charismatic","Expressive","Confident","Engaging"], strengths:"You have a magnetic presence that captivates audiences. Your authenticity shines through in everything you do.", growthAreas:"Develop resilience to criticism. Balance your public persona with intentional private self-care.", careers:["Actor","Presenter","Entertainer","Public Speaker","Influencer"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Strategic Collaborator":{ icon:Users, emoji:"♟️", description:"You excel at building relationships and bringing people together. Your focus on networking and collaboration demonstrates strong interpersonal intelligence.", traits:["Diplomatic","Connected","Strategic","Empathetic"], strengths:"You naturally build bridges between people and ideas. Your network is your greatest asset.", growthAreas:"Set clear boundaries. Learn to say no to preserve energy for high-impact collaborations.", careers:["Partnership Manager","Business Developer","Connector","Network Builder","Community Lead"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Cultural Storyteller":{ icon:BookOpen, emoji:"📖", description:"You are passionate about stories, culture, and preserving meaningful narratives. Your love for reading, writing, and cultural immersion defines your path.", traits:["Thoughtful","Articulate","Curious","Cultured"], strengths:"You understand human nature deeply and can translate it into compelling narratives that resonate.", growthAreas:"Balance consumption with creation. Set deadlines to ship your stories into the world.", careers:["Writer","Content Creator","Storyteller","Author","Journalist"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Impact Innovator":{ icon:Zap, emoji:"🌟", description:"You are motivated by making a meaningful difference. Your emphasis on social justice and innovation shows a commitment to positive change.", traits:["Purpose-driven","Innovative","Compassionate","Bold"], strengths:"You see systems that need changing and have the courage to challenge the status quo.", growthAreas:"Avoid burnout by celebrating small wins. Remember that sustainable impact requires self-care.", careers:["Social Entrepreneur","Change Leader","Impact Founder","Systems Innovator"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Technical Craftsperson":{ icon:Target, emoji:"⚙️", description:"You value mastery, precision, and deep expertise. Your focus on technical skills and academic excellence shows dedication to your craft.", traits:["Precise","Skilled","Focused","Excellent"], strengths:"Your attention to detail and commitment to mastery make you indispensable in specialized fields.", growthAreas:"Share your knowledge more freely. Mentor others to amplify your impact.", careers:["Specialist","Expert Practitioner","Master Craftsperson","Technical Lead"], color:"from-[#2E1A47] to-[#3D265A]" },
  "The Balanced Explorer":{ icon:Globe, emoji:"🌍", description:"You seek variety, adventure, and global experiences. Your balanced approach to life reflects curiosity and open-mindedness.", traits:["Adventurous","Curious","Adaptable","Open-minded"], strengths:"You thrive in diverse environments and can adapt to any culture or situation with ease.", growthAreas:"Deepen your expertise in key areas. Balance exploration with focused mastery.", careers:["Global Professional","Portfolio Career","Multi-Disciplinary","Renaissance Person"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Community Builder":{ icon:Users, emoji:"🏘️", description:"You prioritize relationships, family, and community impact. Your focus on mentoring and workplace culture shows deep care for others.", traits:["Nurturing","Connected","Supportive","Community-focused"], strengths:"You create environments where people feel seen, valued, and empowered to contribute their best.", growthAreas:"Ensure your giving doesn't deplete you. Build systems that scale your care.", careers:["Community Manager","Facilitator","Organizer","Network Leader"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Spiritual Seeker":{ icon:Mountain, emoji:"🧘", description:"You value inner growth, mindfulness, and spiritual development. Your pursuit of meaning beyond the material defines your journey.", traits:["Reflective","Grounded","Wise","Intentional"], strengths:"You bring depth, presence, and perspective that helps others find clarity and peace.", growthAreas:"Ground spiritual insights in practical action. Share your wisdom more publicly.", careers:["Life Coach","Wellness Practitioner","Purpose Guide","Mindfulness Teacher"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Fearless Adventurer":{ icon:Mountain, emoji:"🏔️", description:"You are drawn to extreme experiences and pushing physical limits. Your adventurous spirit seeks thrills and challenges.", traits:["Brave","Bold","Energetic","Risk-taking"], strengths:"Your courage inspires others to face their fears and embrace life fully.", growthAreas:"Channel your energy strategically. Not every risk is worth taking.", careers:["Risk-Taker","Bold Innovator","Adventurer","Boundary-Pusher"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Musical Soul":{ icon:Music, emoji:"🎵", description:"Music and artistic expression are central to who you are. Your creative spirit flows through sound and rhythm.", traits:["Creative","Expressive","Passionate","Artistic"], strengths:"You connect with people emotionally through your art in ways words cannot capture.", growthAreas:"Build the business side of your artistry. Learn to monetize your gift sustainably.", careers:["Musician","Artist","Creative","Performer","Cultural Creator"], color:"from-[#2E1A47] to-[#D4AF37]" },
  "The Resilient Leader":{ icon:Shield, emoji:"🧭", description:"You demonstrate exceptional leadership, confidence, and mental resilience. Your focus on building and leading shows natural authority.", traits:["Strong","Determined","Visionary","Commanding"], strengths:"You remain steady under pressure and inspire confidence in those you lead.", growthAreas:"Lead with vulnerability. Let others see your humanity, not just your strength.", careers:["Executive","CEO","Director","Organizational Leader"], color:"from-[#2E1A47] to-[#D4AF37]" }
};

const determineProfile = (responses) => {
  const scores = {};
  Object.keys(PROFILES).forEach(p => { scores[p] = 0; });
  const allValues = [...responses.experiences.map(r=>r.value),...responses.growth.map(r=>r.value),...responses.contribution.map(r=>r.value)];
  const allText = [...responses.experiences,...responses.growth,...responses.contribution].map(r=>r.label).join(" ").toLowerCase();
  
  allValues.forEach(v => {
    if(["perform_stage","attend_premieres","entertainment_events","performance_skills"].includes(v)){ scores["The Authentic Performer"]+=3; scores["The Visionary Creator"]+=1; }
    if(["artistic_expression","creative_thinking","artistic_skills","create_content","creative_direction"].includes(v)){ scores["The Visionary Creator"]+=3; scores["The Cultural Storyteller"]+=1; }
    if(["social_connections","industry_networking","mentoring","collaborate_artists","mentor_artists"].includes(v)){ scores["The Strategic Collaborator"]+=3; scores["The Community Builder"]+=1; }
    if(["storytelling","cultural_immersion","cultural_storytelling","reading_knowledge","thought_leadership","write_book"].includes(v)){ scores["The Cultural Storyteller"]+=3; scores["The Impact Innovator"]+=1; }
    if(["social_justice","diverse_representation","entertainment_accessibility","innovation","inspire_through_art","mental_health_advocacy","youth_empowerment"].includes(v)){ scores["The Impact Innovator"]+=3; scores["The Cultural Storyteller"]+=1; }
    if(["technical_mastery","media_production","strategic_thinking","academic_excellence"].includes(v)){ scores["The Technical Craftsperson"]+=3; scores["The Visionary Creator"]+=1; }
    if(["travel_world","cultural_immersion","festival_experiences","adventure_sports","nature_wilderness","volunteer_abroad"].includes(v)){ scores["The Balanced Explorer"]+=3; }
    if(["family_moments","mentoring","workplace_culture","volunteering","empathy_compassion","family_legacy"].includes(v)){ scores["The Community Builder"]+=3; }
    if(["spiritual_experiences","spiritual_development","mindfulness"].includes(v)){ scores["The Spiritual Seeker"]+=4; }
    if(["skydive_extreme","athletic_peak","physical_fitness"].includes(v)){ scores["The Fearless Adventurer"]+=3; }
    if(["record_music","artistic_skills"].includes(v)){ scores["The Musical Soul"]+=4; }
    if(["leadership","confidence_courage","mental_resilience","entrepreneurship","build_company"].includes(v)){ scores["The Resilient Leader"]+=3; scores["The Strategic Collaborator"]+=1; }
  });
  
  if(allText.includes("music")||allText.includes("song")) scores["The Musical Soul"]+=3;
  if(allText.includes("meditat")||allText.includes("spirit")||allText.includes("mindful")) scores["The Spiritual Seeker"]+=3;
  if(allText.includes("extreme")||allText.includes("climb")||allText.includes("skydiv")) scores["The Fearless Adventurer"]+=3;
  
  const e=responses.experiences.length, g=responses.growth.length, c=responses.contribution.length;
  if(Math.max(e,g,c)-Math.min(e,g,c)<=2 && (e+g+c)>=30) scores["The Balanced Explorer"]+=4;
  if(c>e&&c>g){ scores["The Impact Innovator"]+=3; scores["The Community Builder"]+=3; }
  if(g>e&&g>c){ scores["The Technical Craftsperson"]+=2; scores["The Visionary Creator"]+=2; }
  if(e>g&&e>c){ scores["The Authentic Performer"]+=2; scores["The Balanced Explorer"]+=2; }
  
  let top="The Balanced Explorer", topScore=0;
  Object.entries(scores).forEach(([p,s])=>{ if(s>topScore){topScore=s;top=p;} });
  return top;
};

const genParagraph = (responses, profileName) => {
  const prof = PROFILES[profileName];
  const e=responses.experiences.length, g=responses.growth.length, c=responses.contribution.length, total=e+g+c;
  let text = `Based on your ${total} selections, you embody ${profileName}. `;
  if(e>g&&e>c) text += "Your emphasis on experiences shows you value living life fully. ";
  if(g>e&&g>c) text += "Your focus on growth reveals a commitment to continuous self-improvement. ";
  if(c>e&&c>g) text += "Your emphasis on contribution demonstrates a desire to make a lasting impact. ";
  text += prof.description;
  return text;
};

const downloadResults = (name, profileName, responses, industry) => {
  const prof = PROFILES[profileName];
  const careers = getCareersForProfile(profileName, industry || "general");
  const assessment = genParagraph(responses, profileName);
  let text = `MiiQ FitFinder ASSESSMENT RESULTS\n${"=".repeat(50)}\n\nName: ${name}\nProfile: ${profileName} ${prof.emoji}\nIndustry: ${industry || "general"}\nDate: ${new Date().toLocaleDateString()}\n\nPERSONALITY ASSESSMENT\n${"-".repeat(30)}\n${assessment}\n\nCORE TRAITS: ${prof.traits.join(", ")}\n\nSTRENGTHS:\n${prof.strengths}\n\nGROWTH AREAS:\n${prof.growthAreas}\n\nIDEAL CAREERS: ${careers.join(", ")}\n\n`;
  QUESTIONS.forEach(q => {
    text += `${q.title.toUpperCase()}\n${"-".repeat(30)}\n`;
    responses[q.key].forEach((r, i) => {
      text += `${i + 1}. ${r.label}${r.custom ? " (Personal)" : ""}\n`;
    });
    text += "\n";
  });
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `MiiQ-FitFinder-${name.replace(/\s+/g, "-")}-Results.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const downloadPDF = async (name, profileName, responses, industry, userPhoto) => {
  // First, capture the vision board as an image
  const visionBoardElement = document.querySelector("[data-vision-board]");
  let visionBoardImage = null;
  
  if (visionBoardElement) {
    try {
      const canvas = await html2canvas(visionBoardElement, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#f8fafc",
        width: 1200,
        windowWidth: 1200
      });
      visionBoardImage = canvas.toDataURL("image/jpeg", 0.9);
    } catch (e) {
      console.log("Could not capture vision board:", e);
    }
  }
  
  
  const prof = PROFILES[profileName];
  const careers = getCareersForProfile(profileName, industry || "general");
  
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4"
  });
  
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 15;
  let y = margin;
  
  pdf.setFillColor(102, 126, 234);
  pdf.rect(0, 0, pageWidth, 60, "F");
  
  if (userPhoto) {
    try {
      // Create an image to get dimensions
      const img = new Image();
      img.src = userPhoto;
      const aspectRatio = img.width / img.height;
      
      const maxSize = 30; // Maximum size in mm
      let photoWidth, photoHeight;
      
      if (aspectRatio > 1) {
        // Landscape photo
        photoWidth = maxSize;
        photoHeight = maxSize / aspectRatio;
      } else {
        // Portrait or square photo
        photoHeight = maxSize;
        photoWidth = maxSize * aspectRatio;
      }
      
      pdf.addImage(userPhoto, "JPEG", margin, 10, photoWidth, photoHeight, undefined, "FAST");
    } catch (e) {
      console.log("Could not add photo to PDF");
    }
  }
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(28);
  pdf.setFont(undefined, "bold");
  pdf.text("MiiQ FitFinder", userPhoto ? 50 : margin, 25);
  
  pdf.setFontSize(14);
  pdf.setFont(undefined, "normal");
  pdf.text(name, userPhoto ? 50 : margin, 35);
  pdf.text(profileName, userPhoto ? 50 : margin, 42);
  
  y = 70;
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(12);
  pdf.setFont(undefined, "bold");
  pdf.text("Your Psychological Profile", margin, y);
  y += 8;
  
  pdf.setFont(undefined, "normal");
  pdf.setFontSize(10);
  const descLines = pdf.splitTextToSize(prof.description, pageWidth - 2 * margin);
  pdf.text(descLines, margin, y);
  y += descLines.length * 5 + 10;
  
  pdf.setFont(undefined, "bold");
  pdf.setFontSize(11);
  pdf.text("Core Traits", margin, y);
  y += 6;
  pdf.setFont(undefined, "normal");
  pdf.setFontSize(10);
  pdf.text(prof.traits.join("   "), margin, y);
  y += 10;
  
  pdf.setFont(undefined, "bold");
  pdf.setFontSize(11);
  pdf.text("Your Strengths", margin, y);
  y += 6;
  pdf.setFont(undefined, "normal");
  pdf.setFontSize(10);
  const strengthLines = pdf.splitTextToSize(prof.strengths, pageWidth - 2 * margin);
  pdf.text(strengthLines, margin, y);
  y += strengthLines.length * 5 + 10;
  
  pdf.setFont(undefined, "bold");
  pdf.setFontSize(11);
  pdf.text("Growth Opportunities", margin, y);
  y += 6;
  pdf.setFont(undefined, "normal");
  pdf.setFontSize(10);
  const growthLines = pdf.splitTextToSize(prof.growthAreas, pageWidth - 2 * margin);
  pdf.text(growthLines, margin, y);
  y += growthLines.length * 5 + 10;
  
  pdf.setFont(undefined, "bold");
  pdf.setFontSize(11);
  pdf.text("Ideal Career Paths", margin, y);
  y += 6;
  pdf.setFont(undefined, "normal");
  pdf.setFontSize(10);
  careers.forEach((career, i) => {
    if (y > pageHeight - 20) {
      pdf.addPage();
      y = margin;
    }
    pdf.text(`${i + 1}. ${career}`, margin + 5, y);
    y += 6;
  });
  
  y += 10;
  
  if (y > pageHeight - 60) {
    pdf.addPage();
    y = margin;
  }
  
  pdf.setFillColor(245, 245, 250);
  pdf.rect(0, y - 5, pageWidth, 10, "F");
  pdf.setFontSize(16);
  pdf.setFont(undefined, "bold");
  pdf.setTextColor(102, 126, 234);
  pdf.text("Your Vision Board", margin, y);
  y += 15;
  
  pdf.setTextColor(0, 0, 0);
  
  if (visionBoardImage) {
    pdf.addPage("a4", "portrait");
    
    pdf.setFillColor(102, 126, 234);
    pdf.rect(0, 0, pageWidth, 25, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont(undefined, "bold");
    pdf.text("YOUR VISION BOARD - Print & Hang", pageWidth / 2, 16, { align: "center" });
    
    const maxWidth = pageWidth - 2 * margin;
    const maxHeight = pageHeight - 40;
    
    try {
      pdf.addImage(visionBoardImage, "JPEG", margin, 30, maxWidth, maxHeight, undefined, "NONE");
    } catch (e) {
      console.log("Could not add vision board to PDF");
    }
  }
  
  pdf.setFontSize(8);
  pdf.setTextColor(128, 128, 128);
  pdf.text("Generated by MiiQ FitFinder   https://miiq-fitfinder.vercel.app", pageWidth / 2, pageHeight - 10, { align: "center" });
  
  // Vision Board Pages - Landscape format
  const landscapeWidth = 297;
  const landscapeHeight = 210;
  
  const questions = [
    { key: "experiences", title: "EXPERIENCES", color1: [147, 51, 234], color2: [219, 39, 119] },
    { key: "growth", title: "GROWTH", color1: [59, 130, 246], color2: [8, 145, 178] },
    { key: "contribution", title: "CONTRIBUTION", color1: [34, 197, 94], color2: [5, 150, 105] }
  ];
  
  questions.forEach((q) => {
    const tiles = responses[q.key] || [];
    if (tiles.length === 0) return;
    
    // Tiles with pagination (8 tiles per page: 4 cols   2 rows)
    const cols = 4;
    const rows = 2;
    const tilesPerPage = cols * rows;
    const tileSize = 60;
    const gap = 8;
    const startX = (landscapeWidth - (cols * tileSize + (cols - 1) * gap)) / 2;
    const startY = 55;
    
    const numPages = Math.ceil(tiles.length / tilesPerPage);
    
    for (let pageNum = 0; pageNum < numPages; pageNum++) {
      // Always add a new page for each iteration
      pdf.addPage("a4", "landscape");
      
      // Header
      pdf.setFillColor(...q.color1);
      pdf.rect(0, 0, landscapeWidth, 40, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(28);
      pdf.setFont(undefined, "bold");
      
      const headerText = numPages > 1 ? `${q.title} (${pageNum + 1}/${numPages})` : q.title;
      pdf.text(headerText, landscapeWidth / 2, 25, { align: "center" });
      
      const startIdx = pageNum * tilesPerPage;
      const endIdx = Math.min(startIdx + tilesPerPage, tiles.length);
      const pageTiles = tiles.slice(startIdx, endIdx);
      
      pageTiles.forEach(async (tile, pageIdx) => {
        const i = startIdx + pageIdx;
        const col = pageIdx % cols;
        const row = Math.floor(pageIdx / cols);
        const x = startX + col * (tileSize + gap);
        const tileY = startY + row * (tileSize + gap);
      
      // Add inspiring image (top 70% of tile)
      try {
        const imageUrl = `https://picsum.photos/seed/${encodeURIComponent(tile.label)}/400/400`;
        const imageHeight = tileSize * 0.70;
        pdf.addImage(imageUrl, "JPEG", x, tileY, tileSize, imageHeight, undefined, "FAST");
        
        // Gradient overlay for readability
        pdf.setFillColor(...q.color1);
        pdf.setGState(new pdf.GState({opacity: 0.3}));
        pdf.rect(x, tileY, tileSize, imageHeight, "F");
        pdf.setGState(new pdf.GState({opacity: 1.0}));
      } catch (e) {
        // Fallback to colored box if image fails
        pdf.setFillColor(...q.color1);
        pdf.rect(x, tileY, tileSize, tileSize * 0.70, "F");
      }
      
      // Bottom section (30%) - solid color for text
      pdf.setFillColor(...q.color2);
      pdf.rect(x, tileY + tileSize * 0.70, tileSize, tileSize * 0.30, "F");
      
      // Number badge (white circle in top-left)
      pdf.setFillColor(255, 255, 255);
      pdf.circle(x + 10, tileY + 10, 6, "F");
      pdf.setTextColor(...q.color1);
      pdf.setFontSize(18);
      pdf.setFont(undefined, "bold");
      pdf.text((i + 1).toString(), x + (i + 1 < 10 ? 7.5 : 6), tileY + 13);
      
      // Custom badge
      if (tile.custom) {
        pdf.setFillColor(255, 215, 0); // Gold
        pdf.circle(x + tileSize - 10, tileY + 10, 5, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.text("?", x + tileSize - 13, tileY + 13);
      }
      
      // Label (white text on colored background)
      pdf.setFontSize(7);
      pdf.setFont(undefined, "bold");
      pdf.setTextColor(255, 255, 255);
      const labelLines = pdf.splitTextToSize(tile.label, tileSize - 6);
      const maxLines = 3;
      const displayLines = labelLines.slice(0, maxLines);
      let textY = tileY + tileSize * 0.72 + 4;
      displayLines.forEach((line) => {
        const lineWidth = pdf.getTextWidth(line);
        const textX = x + (tileSize - lineWidth) / 2;
        pdf.text(line, textX, textY);
        textY += 3.5;
      });
    });
    
    // Footer on each page
    pdf.setFontSize(10);
    pdf.setTextColor(150, 150, 150);
    pdf.text("Print this page   Hang on your wall   Live your vision", landscapeWidth / 2, landscapeHeight - 10, { align: "center" });
  }
});
  
  const fileName = `MiiQ-FitFinder-${name.replace(/\s+/g, "-")}.pdf`;
  const platform = window.Capacitor?.getPlatform?.() || "web";
  if (platform === "android" || platform === "ios") {
    try {
      const pdfBase64 = pdf.output("datauristring").split(",")[1];
      const { Filesystem, Directory } = await import("@capacitor/filesystem");
      const { Share } = await import("@capacitor/share");
      await Filesystem.writeFile({ path: fileName, data: pdfBase64, directory: Directory.Cache });
      const fileResult = await Filesystem.getUri({ directory: Directory.Cache, path: fileName });
      await Share.share({ title: "MiiQ FitFinder Report", url: fileResult.uri, dialogTitle: "Save or Share your PDF" });
    } catch (e) {
      alert("PDF Error: " + (e?.message || String(e)));
    }
  } else {
    pdf.save(fileName);
  }
};

// One-page frameable profile PDF for the Profile screen Download button
const downloadProfilePDF = async (name, profileName, industry, userPhoto) => {
  const prof = PROFILES[profileName];
  const careers = getCareersForProfile(profileName, industry || "general");

  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = 210;
  const pageHeight = 297;

  // Royal Purple gradient header band
  pdf.setFillColor(46, 26, 71); // #2E1A47
  pdf.rect(0, 0, pageWidth, 55, "F");
  // Gold accent stripe
  pdf.setFillColor(212, 175, 55); // #D4AF37
  pdf.rect(0, 55, pageWidth, 3, "F");

  // Brand mark
  pdf.setTextColor(212, 175, 55);
  pdf.setFontSize(11);
  pdf.setFont(undefined, "bold");
  pdf.text("MIIQ FITFINDER", pageWidth / 2, 16, { align: "center" });

  // Name (centered, large)
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(24);
  pdf.setFont(undefined, "bold");
  pdf.text(name, pageWidth / 2, 32, { align: "center" });

  // Profile title (centered, gold)
  pdf.setTextColor(212, 175, 55);
  pdf.setFontSize(16);
  pdf.setFont(undefined, "normal");
  pdf.text(profileName, pageWidth / 2, 45, { align: "center" });

  let y = 75;
  const margin = 22;
  const contentWidth = pageWidth - 2 * margin;

  // Section: Your Psychological Profile
  pdf.setTextColor(46, 26, 71);
  pdf.setFontSize(13);
  pdf.setFont(undefined, "bold");
  pdf.text("Your Psychological Profile", margin, y);
  // Gold underline
  pdf.setDrawColor(212, 175, 55);
  pdf.setLineWidth(0.7);
  pdf.line(margin, y + 2, margin + 60, y + 2);
  y += 10;

  pdf.setTextColor(60, 60, 60);
  pdf.setFontSize(11);
  pdf.setFont(undefined, "normal");
  const descLines = pdf.splitTextToSize(prof.description, contentWidth);
  pdf.text(descLines, margin, y);
  y += descLines.length * 5.5 + 8;

  // Section: Core Traits
  pdf.setTextColor(46, 26, 71);
  pdf.setFontSize(13);
  pdf.setFont(undefined, "bold");
  pdf.text("Core Traits", margin, y);
  pdf.line(margin, y + 2, margin + 30, y + 2);
  y += 9;
  pdf.setTextColor(60, 60, 60);
  pdf.setFontSize(11);
  pdf.setFont(undefined, "normal");
  pdf.text(prof.traits.join("   |   "), margin, y);
  y += 12;

  // Section: Your Strengths
  pdf.setTextColor(46, 26, 71);
  pdf.setFontSize(13);
  pdf.setFont(undefined, "bold");
  pdf.text("Your Strengths", margin, y);
  pdf.line(margin, y + 2, margin + 38, y + 2);
  y += 9;
  pdf.setTextColor(60, 60, 60);
  pdf.setFontSize(11);
  pdf.setFont(undefined, "normal");
  const strengthLines = pdf.splitTextToSize(prof.strengths, contentWidth);
  pdf.text(strengthLines, margin, y);
  y += strengthLines.length * 5.5 + 8;

  // Section: Growth Opportunities
  pdf.setTextColor(46, 26, 71);
  pdf.setFontSize(13);
  pdf.setFont(undefined, "bold");
  pdf.text("Growth Opportunities", margin, y);
  pdf.line(margin, y + 2, margin + 52, y + 2);
  y += 9;
  pdf.setTextColor(60, 60, 60);
  pdf.setFontSize(11);
  pdf.setFont(undefined, "normal");
  const growthLines = pdf.splitTextToSize(prof.growthAreas, contentWidth);
  pdf.text(growthLines, margin, y);
  y += growthLines.length * 5.5 + 8;

  // Section: Ideal Career Paths
  pdf.setTextColor(46, 26, 71);
  pdf.setFontSize(13);
  pdf.setFont(undefined, "bold");
  pdf.text("Ideal Career Paths", margin, y);
  pdf.line(margin, y + 2, margin + 45, y + 2);
  y += 9;
  pdf.setTextColor(60, 60, 60);
  pdf.setFontSize(11);
  pdf.setFont(undefined, "normal");
  careers.forEach((career, i) => {
    if (y > pageHeight - 30) return; // keep to one page
    pdf.text(`${i + 1}.  ${career}`, margin + 3, y);
    y += 6;
  });

  // Footer band
  pdf.setFillColor(46, 26, 71);
  pdf.rect(0, pageHeight - 15, pageWidth, 15, "F");
  pdf.setFillColor(212, 175, 55);
  pdf.rect(0, pageHeight - 15, pageWidth, 1.5, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(9);
  pdf.setFont(undefined, "normal");
  pdf.text("Discover your perfect career path  -  miiq-fitfinder.vercel.app", pageWidth / 2, pageHeight - 6, { align: "center" });

  const fileName = `MiiQ-Profile-${name.replace(/\s+/g, "-")}.pdf`;
  const platform = window.Capacitor?.getPlatform?.() || "web";
  if (platform === "android" || platform === "ios") {
    try {
      const pdfBase64 = pdf.output("datauristring").split(",")[1];
      const { Filesystem, Directory } = await import("@capacitor/filesystem");
      const { Share } = await import("@capacitor/share");
      await Filesystem.writeFile({ path: fileName, data: pdfBase64, directory: Directory.Cache });
      const fileResult = await Filesystem.getUri({ directory: Directory.Cache, path: fileName });
      await Share.share({ title: "MiiQ Profile", url: fileResult.uri, dialogTitle: "Save or Share your Profile" });
    } catch (e) {
      alert("PDF Error: " + (e?.message || String(e)));
    }
  } else {
    pdf.save(fileName);
  }
};

const TileModal = memo(({ tile, question, onClose }) => {
  if (!tile) return null;
  const q = QUESTIONS.find(q => q.key === question);
  const description = getTileDescription(tile.value, tile.label);
  const QIcon = q.icon;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_25px_70px_-15px_rgba(0,0,0,0.5)] max-w-2xl w-full overflow-hidden border border-white/40 transform transition-all" onClick={e => e.stopPropagation()}>
        <div className={"h-56 bg-gradient-to-br " + q.color + " relative flex items-end p-8"}>
          <div className="absolute inset-0 bg-black/10"></div>
          <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close" className="absolute top-6 right-6 p-3 bg-white/30 active:bg-white/50 rounded-2xl backdrop-blur-sm z-20 cursor-pointer" style={{touchAction:'manipulation'}}>
            <X className="w-6 h-6 text-white pointer-events-none" />
          </button>
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <QIcon className="w-5 h-5 text-white" />
              </div>
              <span className="text-white/90 text-sm font-bold uppercase tracking-wider">{q.title}</span>
            </div>
            <h2 className="text-white text-4xl font-black leading-tight drop-shadow-lg">{tile.label}</h2>
            {tile.custom && <span className="inline-block mt-2 text-white/80 text-sm bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">? Your personal goal</span>}
          </div>
        </div>
        <div className="p-10">
          <div className="flex items-center space-x-3 mb-6">
            <div className={"w-1.5 h-12 rounded-full bg-gradient-to-b " + q.color}></div>
            <h3 className="text-2xl font-bold text-gray-800">What This Means</h3>
          </div>
          <p className="text-gray-700 leading-relaxed text-lg mb-8">{description}</p>
          <button onClick={onClose} className={"w-full py-4 rounded-2xl bg-gradient-to-r " + q.color + " text-white font-bold text-lg hover:shadow-xl hover:scale-[1.02] active:scale-100 transition-all"}>
            Continue Your Journey
          </button>
        </div>
      </div>
    </div>
  );
});

const ShareModal = memo(({ name, profileName, responses, industry, onClose }) => {
  const [copied, setCopied] = React.useState(false);
  const [showEmailChoice, setShowEmailChoice] = React.useState(false);
  const prof = PROFILES[profileName];
  const careers = getCareersForProfile(profileName, industry);
  const shareText = `I just completed my MiiQ FitFinder assessment!\n\nMy profile: ${profileName}\n\nDiscover yours at: https://miiq-fitfinder.vercel.app`;
  
  const handleTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent("I just discovered my MiiQ FitFinder profile: " + profileName + "! #MiiQFitFinder #LifeGoals")}&url=${encodeURIComponent("https://miiq-fitfinder.vercel.app")}`, "_blank");
  };
  
  const handleLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://miiq-fitfinder.vercel.app")}`, "_blank");
  };
  
  const handleFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://miiq-fitfinder.vercel.app")}&quote=${encodeURIComponent(shareText)}`, "_blank");
  };
  
  const handleEmailFull = () => {
    setShowEmailChoice(true);
  };
  
  const sendEmailVia = (platform) => {
    const totalGoals = responses.experiences.length + responses.growth.length + responses.contribution.length;
    const today = new Date().toLocaleDateString();
    const emailSubject = `MiiQ FitFinder Assessment Results - ${profileName}`;
    
    const fullReport = [
      "MiiQ FitFinder ASSESSMENT RESULTS",
      "==================================================",
      `Name: ${name}`,
      `Profile: ${profileName} ${prof.emoji}`,
      `Industry: ${industry || 'Not specified'}`,
      `Date: ${today}`,
      "",
      "PERSONALITY ASSESSMENT",
      "------------------------------",
      `Based on your ${totalGoals} selections, you embody ${profileName}. ${prof.description}`,
      "",
      `CORE TRAITS: ${prof.traits.join(", ")}`,
      "",
      "STRENGTHS:",
      prof.strengths,
      "",
      "GROWTH AREAS:",
      prof.growthAreas,
      "",
      `IDEAL CAREERS: ${careers.join(", ")}`,
      "",
      "EXPERIENCES: WHAT DO YOU WANT TO EXPERIENCE?",
      "------------------------------",
      ...responses.experiences.map((item, i) => `${i + 1}. ${item.label}`),
      "",
      "GROWTH: HOW DO YOU WANT TO GROW?",
      "------------------------------",
      ...responses.growth.map((item, i) => `${i + 1}. ${item.label}`),
      "",
      "CONTRIBUTION: WHAT DO YOU WANT TO CONTRIBUTE?",
      "------------------------------",
      ...responses.contribution.map((item, i) => `${i + 1}. ${item.label}`),
      "",
      "==================================================",
      "Discover YOUR profile at: https://miiq-fitfinder.vercel.app"
    ].join("\n");
    
    if (platform === 'gmail') {
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(fullReport)}`;
      const emailWindow = window.open(gmailUrl, '_blank', 'width=800,height=600');
      
      if (!emailWindow) {
        navigator.clipboard.writeText(fullReport).then(() => {
          alert('Popup blocked! Your full report has been copied to clipboard.\n\nPlease paste it into Gmail.');
        }).catch(() => {
          alert('Please allow popups to email the full report.');
        });
      }
    } else if (platform === 'outlook') {
      // Outlook has URL length limits, so copy to clipboard and open compose
      navigator.clipboard.writeText(fullReport).then(() => {
        // Show alert FIRST, then open Outlook
        alert('? Report copied to clipboard!\n\nOutlook will open next - paste the report into the email body (Ctrl+V or Cmd+V).');
        
        // Then open Outlook compose with just subject (body would be too long)
        setTimeout(() => {
          const outlookUrl = `https://outlook.office.com/mail/deeplink/compose?subject=${encodeURIComponent(emailSubject)}`;
          window.open(outlookUrl, '_blank', 'width=800,height=600');
        }, 100); // Small delay to ensure alert is visible first
      }).catch(() => {
        alert('Unable to copy report. Please allow clipboard access.');
      });
    }
    
    setShowEmailChoice(false);
  };
  
  const handleCopy = () => {
    navigator.clipboard.writeText(shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-[0_25px_70px_-15px_rgba(0,0,0,0.4)] border border-white/40 max-w-md w-full p-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-black text-gray-800">Share Your Results</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>
        <div className="space-y-3">
          <button onClick={handleTwitter} className="w-full py-4 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white font-bold rounded-2xl transition-all flex items-center justify-center space-x-2">
            <Twitter className="w-5 h-5" />
            <span>Share on Twitter</span>
          </button>
          <button onClick={handleLinkedIn} className="w-full py-4 bg-[#0A66C2] hover:bg-[#004182] text-white font-bold rounded-2xl transition-all flex items-center justify-center space-x-2">
            <Linkedin className="w-5 h-5" />
            <span>Share on LinkedIn</span>
          </button>
          <button onClick={handleFacebook} className="w-full py-4 bg-[#1877F2] hover:bg-[#166fe5] text-white font-bold rounded-2xl transition-all flex items-center justify-center space-x-2">
            <Share2 className="w-5 h-5" />
            <span>Share on Facebook</span>
          </button>
          <button onClick={handleEmailFull} className="w-full py-4 bg-gradient-to-r from-[#2E1A47] to-[#3D265A] hover:from-indigo-700 hover:to-purple-700 text-white font-bold rounded-2xl transition-all flex items-center justify-center space-x-2 border-2 border-yellow-400 shadow-lg">
            <Mail className="w-5 h-5" />
            <span>Email Full Report (Gmail/Outlook)</span>
          </button>
          <button onClick={handleCopy} className="w-full py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold rounded-2xl transition-all flex items-center justify-center space-x-2">
            {copied ? <Check className="w-5 h-5 text-green-600" /> : <Copy className="w-5 h-5" />}
            <span>{copied ? "Copied!" : "Copy Link"}</span>
          </button>
        </div>
      </div>
      
      {showEmailChoice && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-3xl flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl">
            <h4 className="text-xl font-bold text-gray-800 mb-4 text-center">Choose Your Email Platform</h4>
            <p className="text-gray-600 text-sm mb-6 text-center">Select your preferred email service to send the full report</p>
            <div className="space-y-3">
              <button onClick={() => sendEmailVia('gmail')} className="w-full py-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold rounded-xl transition-all flex items-center justify-center space-x-2">
                <Mail className="w-5 h-5" />
                <span>Gmail</span>
              </button>
              <button onClick={() => sendEmailVia('outlook')} className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-xl transition-all flex items-center justify-center space-x-2">
                <Mail className="w-5 h-5" />
                <span>Outlook</span>
              </button>
              <button onClick={() => setShowEmailChoice(false)} className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

const WelcomeScreen = ({ userName, userRole, onNameChange, onRoleChange, onStart, teamCount, onViewTeam, industry, onIndustryChange, userPhoto, onPhotoUpload, hasSavedProgress, clearProgress, onSignOut, onDeleteAccount, showDeleteConfirm, setShowDeleteConfirm, deleteInProgress }) => {
  const [showResume, setShowResume] = React.useState(false);
  
  React.useEffect(() => {
    const checkSaved = () => {
      try {
        const saved = localStorage.getItem('miiq-assessment-draft');
        if (!saved) {
          setShowResume(false);
          return;
        }
        const data = JSON.parse(saved);
        const daysOld = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
        setShowResume(daysOld <= 7);
      } catch {
        setShowResume(false);
      }
    };
    
    checkSaved();
    const interval = setInterval(checkSaved, 1000);
    return () => clearInterval(interval);
  }, []);
  
  return (
  <div className="min-h-screen flex items-center justify-center p-4" style={{background:"radial-gradient(circle at 20% 50%, #667eea 0%, transparent 50%), radial-gradient(circle at 80% 80%, #f093fb 0%, transparent 50%), linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)"}}>
    <div className="max-w-4xl w-full bg-white/80 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl p-8 md:p-12">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-[#2E1A47] via-[#3D265A] to-[#D4AF37] rounded-3xl mb-6 shadow-[0_20px_60px_-15px_rgba(168,85,247,0.4)] animate-float">
          <Sparkles className="w-12 h-12 text-white"/>
        </div>
        
        <div className="mb-6">
          <div className="flex flex-col items-center">
            <div className="relative group mb-4">
              {userPhoto ? (
                <img src={userPhoto} alt="Profile" className="w-40 h-40 rounded-full object-cover border-6 border-white shadow-[0_15px_50px_-12px_rgba(0,0,0,0.3)] ring-4 ring-[#4B3663]/30 group-hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] transition-all duration-300" />
              ) : (
                <div className="w-40 h-40 rounded-full bg-gradient-to-br from-[#4B3663]/30 to-[#3D265A]/20 border-6 border-white shadow-[0_15px_50px_-12px_rgba(0,0,0,0.3)] ring-4 ring-[#4B3663]/30 flex items-center justify-center group-hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] transition-all duration-300">
                  <Upload className="w-12 h-12 text-indigo-400" />
                </div>
              )}
              <label className="absolute bottom-0 right-0 w-10 h-10 bg-gradient-to-br from-[#2E1A47] to-[#3D265A] rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-all shadow-lg"><Plus className="w-5 h-5 text-white" /><input type="file" accept=".jpg,.jpeg,.png,.heic,.webp" onChange={onPhotoUpload} className="hidden" /></label>
            </div>
            {userPhoto && Capacitor.getPlatform() !== 'ios' && (<button onClick={(e) => { e.target.value = null; onPhotoUpload({ target: { files: [] } }); }} className="text-xs text-gray-400 hover:text-red-500 transition-colors">Remove photo</button>)}
          </div>
        </div>
        
        <h1 className="text-6xl md:text-7xl font-black mb-4 bg-gradient-to-r from-[#2E1A47] via-[#3D265A] to-[#D4AF37] bg-clip-text text-transparent tracking-tight animate-gradient bg-[length:200%_auto]">MiiQ FitFinder</h1>
        <p className="text-2xl text-gray-700 mb-2 font-semibold">Find Your Perfect Fit</p>
       <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">Discover your unique Team Fit, Company Fit and Career Fit</p>
      </div>
      
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Select Your Industry</label>
        <select value={industry} onChange={(e) => onIndustryChange(e.target.value)} className="w-full px-6 py-5 rounded-2xl border-2 border-gray-200/50 bg-gradient-to-r from-white to-gray-50 focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/50 focus:outline-none focus:shadow-xl text-lg font-medium transition-all duration-300 ease-in-out cursor-pointer hover:border-[#D4AF37]/60">
          {INDUSTRIES.map(ind => (
            <option key={ind.value} value={ind.value}>{ind.label}</option>
          ))}
        </select>
      </div>
      
      <div className="space-y-5 mb-8">
        <input value={userName} onChange={(e) => onNameChange(e.target.value)} placeholder="Your Name" className="w-full px-6 py-5 rounded-2xl border-2 border-gray-200/50 bg-white/50 focus:bg-white focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/50 focus:shadow-lg focus:outline-none text-lg transition-all duration-300 ease-in-out"/>
        <input value={userRole} onChange={(e) => onRoleChange(e.target.value)} placeholder="Your Role (optional)" className="w-full px-6 py-5 rounded-2xl border-2 border-gray-200/50 bg-white/50 focus:bg-white focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/50 focus:shadow-lg focus:outline-none text-lg transition-all duration-300 ease-in-out"/>
      </div>
      
      {showResume && (
        <button onClick={() => onStart(true)} className="w-full py-6 mb-4 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-green-700 hover:to-emerald-700 text-white text-xl font-bold rounded-2xl shadow-lg hover:shadow-xl hover:scale-[1.02] hover:-translate-y-1 active:scale-100 active:translate-y-0 transition-all duration-300 ease-out flex items-center justify-center space-x-2">
          <span>🔄 Resume Saved Assessment</span>
        </button>
      )}
      
      <button onClick={() => onStart(false)} disabled={false} className="w-full py-6 bg-gradient-to-r from-[#2E1A47] via-[#3D265A] to-[#D4AF37] text-white text-xl font-bold rounded-2xl hover:shadow-[0_20px_60px_-15px_rgba(99,102,241,0.5)] hover:scale-[1.02] hover:-translate-y-1 active:scale-100 active:translate-y-0 transition-all duration-300 ease-out disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
        <span>Begin Your Journey</span>
        <ChevronRight className="w-6 h-6"/>
      </button>
      
      {showResume && (
        <button onClick={() => { clearProgress(); window.location.reload(); }} className="w-full py-2 px-6 text-sm text-gray-500 hover:text-red-500 transition-colors mt-2">
          Start Fresh (Clear Saved Progress)
        </button>
      )}
      
      {teamCount > 0 && (
        <button onClick={onViewTeam} className="w-full mt-4 py-4 bg-white/50 hover:bg-white/80 text-gray-700 font-semibold rounded-2xl border-2 border-gray-200/60 hover:border-[#D4AF37]/60 transition-all">
          View Team Dashboard ({teamCount} {teamCount === 1 ? "member" : "members"})
        </button>
      )}
      <button onClick={onSignOut} className="w-full mt-4 py-3 bg-transparent text-gray-500 hover:text-gray-700 text-sm font-medium underline transition-all">
        Sign Out
      </button>
      <button onClick={() => setShowDeleteConfirm(true)} className="w-full mt-2 py-3 bg-transparent text-red-500 hover:text-red-700 text-sm font-medium underline transition-all">
        Delete Account
      </button>
      <div className="mt-6 pt-6 border-t border-gray-200/50 flex items-center justify-center gap-4 text-xs text-gray-500">
        <a href="/privacy" className="hover:text-[#D4AF37] transition-colors">Privacy</a>
        <span className="text-gray-300">·</span>
        <a href="/terms" className="hover:text-[#D4AF37] transition-colors">Terms</a>
        <span className="text-gray-300">·</span>
        <a href="/support" className="hover:text-[#D4AF37] transition-colors">Support</a>
      </div>
    </div>
    {showDeleteConfirm && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-3xl">⚠️</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Delete Account?</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              This will <strong>permanently delete</strong> your account and all assessment data. This action cannot be undone.
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={onDeleteAccount}
              disabled={deleteInProgress}
              className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleteInProgress ? "Deleting..." : "Yes, Delete My Account"}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteInProgress}
              className="w-full py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-2xl transition-all disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
  );
};

const AssessmentScreen = memo(({ question, responses, onResponseToggle, custom, onCustomChange, onNext, onBack, time, running }) => {
  const q = QUESTIONS[question];
  const QIcon = q.icon;
  const rs = responses[q.key] || [];
  const OPTIONS = getOptionsForIndustry(window.currentIndustry || "general");
  const opts = OPTIONS[q.key] || [];
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  const isLowTime = time < 60;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E1A47]/5 via-[#3D265A]/10 to-[#D4AF37]/5 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-xl border-b border-gray-200/60 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] rounded-2xl mb-8 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className={"p-3 rounded-xl bg-gradient-to-br " + q.color}>
                <QIcon className="w-6 h-6 text-white"/>
              </div>
              <div>
                <h2 className="text-2xl font-black text-gray-800">{q.title.split(":")[0]}</h2>
                <p className="text-sm text-gray-500">Question {question + 1} of {QUESTIONS.length}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className={`w-5 h-5 ${isLowTime ? "text-red-500" : "text-gray-400"}`}/>
              <span className={`text-lg font-bold ${isLowTime ? "text-red-500 animate-pulse drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]" : "text-gray-700"}`}>
                {minutes}:{seconds.toString().padStart(2, "0")}
              </span>
            </div>
          </div>
          <div className="flex space-x-2">
            {QUESTIONS.map((_, i) => (
              <div key={i} className="flex-1 h-2 rounded-full overflow-hidden bg-gradient-to-r from-gray-100 to-gray-200 shadow-inner">
                <div className={`h-full rounded-full bg-gradient-to-r ${q.color} shadow-lg transition-all duration-500 ${i < question ? "w-full" : i === question ? "w-1/2" : "w-0"}`}></div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-white/40 p-8 hover:shadow-[0_25px_70px_-15px_rgba(0,0,0,0.35)] transition-all duration-300 mb-6">
          <p className="text-xl text-gray-700 leading-relaxed mb-6">{q.title.split(": ")[1]}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {opts.map(opt => {
              const selected = rs.some(r => r.value === opt.value);
              return (
                <button key={opt.value} onClick={() => onResponseToggle(q.key, opt)} className={`p-4 rounded-2xl bg-gradient-to-r from-gray-50 to-white group hover:from-purple-50 hover:to-pink-50 hover:shadow-md border border-gray-100/50 text-left transition-all ${selected ? "ring-2 ring-[#D4AF37] bg-gradient-to-r from-[#4B3663]/10 to-[#D4AF37]/10" : ""}`}>
                  <span className="text-gray-800 font-medium group-hover:text-purple-700">{opt.label}</span>
                </button>
              );
            })}
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-200">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Add Your Own (Optional)</label>
            <div className="flex space-x-3">
              <input value={custom} onChange={(e) => onCustomChange(e.target.value)} placeholder="Type your personal goal..." className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-400 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all"/>
              <button onClick={() => { if (custom.trim()) { onResponseToggle(q.key, { value: `custom_${Date.now()}`, label: custom.trim(), custom: true }); onCustomChange(""); } }} disabled={!custom.trim()} className="px-6 py-3 bg-gradient-to-r from-[#2E1A47] to-[#D4AF37] text-white font-bold rounded-xl hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                Add
              </button>
            </div>
          </div>
          
          <div className="mt-6 flex flex-wrap gap-2">
            {rs.map((r, i) => (
              <div key={i} className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#4B3663]/20 to-[#D4AF37]/20 rounded-full">
                <span className="text-sm font-medium text-purple-900">{r.label}</span>
                <button onClick={() => onResponseToggle(q.key, r)} className="p-1 hover:bg-red-200 rounded-full transition-colors">
                  <X className="w-4 h-4 text-red-600"/>
                </button>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex space-x-4">
          {question > 0 && (
            <button onClick={onBack} className="flex-1 py-4 bg-white text-gray-700 font-bold rounded-2xl border-2 border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all">
              Back
            </button>
          )}
          <button onClick={onNext} disabled={rs.length === 0} className="flex-1 py-4 bg-gradient-to-r from-[#2E1A47] via-[#3D265A] to-[#D4AF37] text-white font-bold rounded-2xl hover:shadow-xl hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            {question === QUESTIONS.length - 1 ? "Complete Assessment" : "Next Question"}
          </button>
        </div>
      </div>
    </div>
  );
});

const ProfileScreen = memo(({ userName, userRole, profileName, responses, industry, userPhoto, isPremium, onViewResults, onReset, onShare, onDownload, onShowPaywall }) => {  const baseProf = PROFILES[profileName];
  const industryCareers = getCareersForProfile(profileName, industry || "general");
  const prof = { ...baseProf, careers: industryCareers };
  const PI = prof.icon;
  const assessment = genParagraph(responses, profileName);
  const total = responses.experiences.length + responses.growth.length + responses.contribution.length;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2E1A47]/5 via-[#3D265A]/10 to-[#D4AF37]/5 relative before:absolute before:inset-0 before:bg-gradient-to-t before:from-white/40 before:to-transparent before:pointer-events-none p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          {userPhoto ? (
            <img src={userPhoto} alt={userName} className="w-48 h-48 rounded-full object-cover border-8 border-white shadow-[0_25px_70px_-15px_rgba(0,0,0,0.4)] mb-8 ring-4 ring-purple-200 mx-auto" />
          ) : (
            <div className={"inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br " + prof.color + " rounded-3xl mb-6 shadow-xl"}>
              <PI className="w-12 h-12 text-white"/>
            </div>
          )}
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/80 rounded-full shadow-sm mb-4">
            
            <span className="text-sm font-semibold text-gray-600">Psychological Profile</span>
          </div>
          <h1 className={"text-4xl md:text-5xl font-black mb-2 bg-gradient-to-r " + prof.color + " bg-clip-text text-transparent"}><span className="mr-3 not-italic">{prof.emoji}</span>{profileName}</h1>
          <p className="text-gray-600 text-lg">{userName}   {total} Life Goals Defined</p>
        </div>
        
        <div className="space-y-6 mb-8">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-white/40 p-8">
            <h2 className="text-2xl font-black mb-4 text-gray-800">Profile Overview</h2>
            <p className="text-gray-700 leading-relaxed mb-4">{assessment}</p>
          </div>
          
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-white/40 p-8">
            <h2 className="text-2xl font-black mb-4 text-gray-800">Core Traits</h2>
            <div className="flex flex-wrap gap-3">
              {prof.traits.map((trait, i) => (
                <span key={i} className={"px-6 py-3 rounded-full bg-gradient-to-r " + prof.color + " shadow-lg hover:shadow-xl text-white font-bold"}>{trait}</span>
              ))}
            </div>
          </div>
          
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-white/40 p-8">
            <h2 className="text-2xl font-black mb-4 text-gray-800">Your Strengths</h2>
            <p className="text-gray-700 leading-relaxed">{prof.strengths}</p>
          </div>
          
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-white/40 p-8">
            <h2 className="text-2xl font-black mb-4 text-gray-800">Growth Opportunities</h2>
            <p className="text-gray-700 leading-relaxed">{prof.growthAreas}</p>
          </div>
          
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-white/40 p-8">
            <div className="flex items-center justify-between mb-4">
  <h2 className="text-2xl font-black text-gray-800">Ideal Career Paths</h2>
  {!isPremium && (
    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">
      FREE: 3 of {prof.careers.length}
    </span>
  )}
</div>
<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
  {(isPremium ? prof.careers : prof.careers.slice(0, 3)).map((career, i) => (
                <div key={i} className="flex items-center space-x-3 p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100">
                  <div className={"w-8 h-8 rounded-lg bg-gradient-to-br " + prof.color + " flex items-center justify-center flex-shrink-0"}>
                    <span className="text-white font-bold text-sm">{i + 1}</span>
                  </div>
                  <span className="text-gray-800 font-medium">{career}</span>
                </div>
              ))}
{!isPremium && (
  <div className="col-span-full mt-4">
    <button
      onClick={onShowPaywall}
      className="w-full py-4 bg-gradient-to-r from-[#2E1A47] via-[#3D265A] to-[#D4AF37] hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center space-x-2"
    >
      <span>🔓 Unlock All {prof.careers.length} Careers - $9.99</span>
    </button>
  </div>
)}
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4">
         {isPremium ? (
  <button onClick={onViewResults} className="flex-1 py-4 bg-gradient-to-r from-[#2E1A47] via-[#3D265A] to-[#D4AF37] text-white font-bold rounded-2xl hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center space-x-2">
    <Eye className="w-5 h-5"/>
    <span>View Vision Board</span>
  </button>
) : (
  <button onClick={onShowPaywall} className="flex-1 py-4 bg-gray-300 text-gray-500 font-bold rounded-2xl cursor-not-allowed flex items-center justify-center space-x-2">
    <Eye className="w-5 h-5"/>
    <span>Vision Board ✨</span>
  </button>
)}
{isPremium ? (
  <button onClick={onShare} className="flex-1 py-4 bg-gradient-to-br from-white to-gray-50 text-gray-700 font-bold rounded-2xl border-2 border-gray-200/60 hover:border-[#D4AF37]/60 hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center space-x-2">
    <Share2 className="w-5 h-5"/>
    <span>Share</span>
  </button>
) : (
  <button onClick={onShowPaywall} className="flex-1 py-4 bg-gray-100 text-gray-400 font-bold rounded-2xl border-2 border-gray-200 cursor-not-allowed flex items-center justify-center space-x-2">
    <Share2 className="w-5 h-5"/>
    <span>Share 📤</span>
  </button>
)}
{isPremium ? (
  <button onClick={onDownload} className="flex-1 py-4 bg-gradient-to-br from-white to-gray-50 text-gray-700 font-bold rounded-2xl border-2 border-gray-200/60 hover:border-[#D4AF37]/60 hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center space-x-2"><Download className="w-5 h-5"/><span>Download PDF</span></button>
) : (
  <button onClick={onShowPaywall} className="flex-1 py-4 bg-gray-100 text-gray-400 font-bold rounded-2xl border-2 border-gray-200 cursor-not-allowed flex items-center justify-center space-x-2">
    <Download className="w-5 h-5"/>
    <span>Download 📥</span>
  </button>
)}
          <button onClick={onReset} className="py-4 px-6 bg-gradient-to-br from-white to-gray-50 text-gray-700 font-bold rounded-2xl border-2 border-gray-200/60 hover:border-[#D4AF37]/60 hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center space-x-2">
            <RotateCcw className="w-5 h-5"/>
          </button>
        </div>
      </div>
    </div>
  );
});

const ResultsScreen = memo(({ userName, userRole, responses, profileName, userPhoto, industry, onViewProfile, onReset, onShare, onDownload, onDownloadPDF }) => {
  const prof = profileName ? PROFILES[profileName] : null;
  const [selectedTile, setSelectedTile] = React.useState(null);
  const [selectedQuestion, setSelectedQuestion] = React.useState(null);
  
  const handleTileClick = (tile, qKey) => {
    setSelectedTile(tile);
    setSelectedQuestion(qKey);
  };
  
  return (
    <>
      {selectedTile && <TileModal tile={selectedTile} question={selectedQuestion} onClose={() => { setSelectedTile(null); setSelectedQuestion(null); }} />}
      <div className="min-h-screen bg-gradient-to-br from-[#2E1A47]/5 via-[#3D265A]/10 to-[#4B3663]/5 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            {userPhoto && (
              <img src={userPhoto} alt={userName} className="w-32 h-32 rounded-full object-cover border-6 border-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] mx-auto mb-6 ring-4 ring-indigo-200" />
            )}
            <h1 className="text-4xl md:text-5xl font-black mb-3 bg-gradient-to-r from-[#2E1A47] to-[#D4AF37] bg-clip-text text-transparent">Your MiiQ FitFinder Vision Board</h1>
            <p className="text-xl text-gray-600 font-medium">{userName}</p>
            {prof && <p className="text-gray-500">{prof.emoji} {profileName}</p>}
          </div>
          
          <div className="space-y-8 mb-8">
            {QUESTIONS.map((q, qIdx) => {
              const rs = responses[q.key] || [];
              if (rs.length === 0) return null;
              const QIcon = q.icon;
              return (
                <div key={q.key} className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-white/40 p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className={"p-3 rounded-xl bg-gradient-to-br " + q.color}>
                      <QIcon className="w-6 h-6 text-white"/>
                    </div>
                    <h2 className="text-2xl font-black text-gray-800">{q.title.split(":")[0]}</h2>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {rs.map((r, i) => (
                      <button key={r.value} onClick={() => handleTileClick(r, q.key)} className="group relative rounded-2xl overflow-hidden shadow-[0_10px_40px_-10px_rgba(0,0,0,0.25)] hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)] transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 w-full cursor-pointer" style={{aspectRatio:"1"}}>
                        <div className={"absolute inset-0 bg-gradient-to-br " + q.color}/>
                        <img src={`https://picsum.photos/seed/${encodeURIComponent(r.label)}/400/400`} alt="" decoding="async" onError={(e)=>{e.currentTarget.style.display='none';}} className="absolute inset-0 w-full h-full object-cover"/>
                        <div className={"absolute inset-0 bg-gradient-to-br opacity-60 mix-blend-multiply " + q.color}/>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"/>
                        <div className="absolute inset-0 p-4 flex flex-col justify-between">
                          <div className="flex justify-between items-start">
                            <div className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                              <span className="text-sm font-black text-gray-800">{i + 1}</span>
                            </div>
                            {r.custom && <span className="px-2 py-1 bg-purple-500/90 backdrop-blur-sm rounded-full text-xs font-bold text-white">?</span>}
                          </div>
                          <div>
                            <p className="text-white font-bold text-sm leading-tight drop-shadow-lg">{r.label}</p>
                          </div>
                        </div>
                        <div className="absolute top-2 right-2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Eye className="w-5 h-5 text-white" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button onClick={onDownloadPDF} className="flex-1 py-4 bg-gradient-to-r from-[#2E1A47] via-[#3D265A] to-[#D4AF37] text-white font-bold rounded-2xl hover:shadow-[0_20px_60px_-15px_rgba(99,102,241,0.5)] hover:scale-[1.02] hover:-translate-y-1 transition-all flex items-center justify-center space-x-2">
              <FileText className="w-5 h-5"/>
              <span>Download PDF Vision Board</span>
            </button>
            
            {prof && (
              <button onClick={onViewProfile} className="flex-1 py-4 bg-gradient-to-br from-white to-gray-50 text-gray-700 font-bold rounded-2xl border-2 border-gray-200/60 hover:border-[#D4AF37]/60 hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center space-x-2">
                <User className="w-5 h-5"/>
                <span>View Profile</span>
              </button>
            )}
            <button onClick={onShare} className="flex-1 py-4 bg-gradient-to-br from-white to-gray-50 text-gray-700 font-bold rounded-2xl border-2 border-gray-200/60 hover:border-[#D4AF37]/60 hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center space-x-2">
              <Share2 className="w-5 h-5"/>
              <span>Share</span>
            </button>
            <button onClick={onReset} className="py-4 px-6 bg-gradient-to-br from-white to-gray-50 text-gray-700 font-bold rounded-2xl border-2 border-gray-200/60 hover:border-[#D4AF37]/60 hover:shadow-xl hover:-translate-y-1 transition-all">
              <RotateCcw className="w-5 h-5"/>
            </button>
          </div>
        </div>
      </div>
    </>
  );
});

const TeamDashboardScreen = memo(({ teamMembers, onAddMember, onViewMember, onBack }) => {
  console.log('[TeamDashboard] RENDER START, teamMembers:', teamMembers);
  const safeMembers = Array.isArray(teamMembers) ? teamMembers : [];
  console.log('[TeamDashboard] safeMembers length:', safeMembers.length);
  const profileGroups = {};
  safeMembers.forEach(m => {
    if (!m) return;
    const p = m.profile || "Unknown";
    if (!profileGroups[p]) profileGroups[p] = [];
    profileGroups[p].push(m);
  });
  console.log('[TeamDashboard] profileGroups keys:', Object.keys(profileGroups));
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-black mb-3 bg-gradient-to-r from-[#2E1A47] to-[#D4AF37] bg-clip-text text-transparent">Team Dashboard</h1>
          <p className="text-xl text-gray-600">{safeMembers.length} {safeMembers.length === 1 ? "Member" : "Members"}</p>
          {onBack && (
            <button onClick={onBack} className="mt-4 px-6 py-2 bg-white border-2 border-[#2E1A47] text-[#2E1A47] font-bold rounded-xl hover:bg-[#2E1A47] hover:text-white transition-all">
              Back to Home
            </button>
          )}
        </div>
        {safeMembers.length === 0 && (
          <div className="bg-white rounded-3xl shadow-lg p-12 text-center">
            <p className="text-gray-500 text-lg">No team members yet. Complete an assessment to add the first member.</p>
          </div>
        )}
        
        <div className="space-y-8">
          {Object.entries(profileGroups).map(([pName, members]) => {
            const pd = PROFILES[pName];
            if (!pd) return null;
            const PIcon = pd.icon;
            return (
              <div key={pName} className="bg-white rounded-3xl shadow-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className={"p-3 rounded-xl bg-gradient-to-br " + pd.color}>
                    <PIcon className="w-6 h-6 text-white"/>
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-gray-800">{pName}</h2>
                    <p className="text-sm text-gray-500">{members.length} {members.length === 1 ? "member" : "members"}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {members.map(member => {
                    const pd = member.profile ? PROFILES[member.profile] : null;
                    return (
                      <div key={member.id} className="bg-gradient-to-br from-white to-gray-50/50 rounded-3xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)] hover:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-gray-100/50 p-6 transition-all cursor-pointer" onClick={() => onViewMember(member)}>
                        <div className="flex items-center space-x-3 mb-3">
                          {pd && <span className="text-2xl">{pd.emoji}</span>}
                          <div>
                            <h3 className="font-bold text-gray-800">{member.name}</h3>
                            {member.role && <p className="text-sm text-gray-500">{member.role}</p>}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400">
                          {((member.responses?.experiences?.length || 0) + (member.responses?.growth?.length || 0) + (member.responses?.contribution?.length || 0))} goals
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        
        <button onClick={onAddMember} className="w-full mt-8 py-4 bg-gradient-to-r from-[#2E1A47] to-[#D4AF37] text-white font-bold rounded-2xl hover:shadow-xl hover:scale-[1.02] transition-all">
          Add Another Member
        </button>
      </div>
    </div>
  );
});

const MiiQFitFinderApp = () => {
  const [screen, setScreen] = React.useState("info");
  const [name, setName] = React.useState("");
  const [role, setRole] = React.useState("");
  const [industry, setIndustry] = React.useState("general");
  const [userPhoto, setUserPhoto] = React.useState(null);
  const [qIdx, setQIdx] = React.useState(0);
  const [responses, setResponses] = React.useState({ experiences: [], growth: [], contribution: [] });
  const [custom, setCustom] = React.useState("");
  const [profile, setProfile] = React.useState(null);
  const [team, setTeam] = React.useState([]);
  const [showShare, setShowShare] = React.useState(false);
  const [time, setTime] = React.useState(300);
  const [running, setRunning] = React.useState(false);
  const [userInfo, setUserInfo] = React.useState({ name: '', email: '', phone: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [deleteInProgress, setDeleteInProgress] = React.useState(false);

  const handleDeleteAccount = async () => {
    if (!userInfo.email) {
      alert("No account found to delete.");
      setShowDeleteConfirm(false);
      return;
    }
    setDeleteInProgress(true);
    try {
      const result = await deleteUserAccount(userInfo.email);
      if (!result.success) {
        alert("Failed to delete account: " + (result.error || "unknown error") + ". Please contact support@miiqfitfinder.com");
        setDeleteInProgress(false);
        return;
      }
      localStorage.removeItem('miiq-user-email');
      localStorage.removeItem('miiq-assessment-draft');
      setName("");
      setRole("");
      setProfile(null);
      setResponses({ experiences: [], growth: [], contribution: [] });
      setCustom("");
      setUserPhoto(null);
      setQIdx(0);
      setTime(300);
      setRunning(false);
      setHasCollectedInfo(false);
      setIsPremium(false);
      setUserInfo({ name: '', email: '', phone: '' });
      setShowDeleteConfirm(false);
      setDeleteInProgress(false);
      alert("Your account has been deleted successfully.");
      setInfoContext('onboarding');
      setScreen("info");
    } catch (err) {
      console.error('Delete account error:', err);
      alert("An error occurred. Please contact support@miiqfitfinder.com");
      setDeleteInProgress(false);
    }
  };
const [isPremium, setIsPremium] = React.useState(false);
const [showPaywall, setShowPaywall] = React.useState(false);
  const [pendingPaywall, setPendingPaywall] = React.useState(false);
const [hasCollectedInfo, setHasCollectedInfo] = React.useState(false);
  const [infoContext, setInfoContext] = React.useState('onboarding');

  // iOS-specific initialization: skip info form (Apple compliance), restore premium
  React.useEffect(() => {
    const initIOS = async () => {
      const platform = Capacitor.getPlatform();
      if (platform !== 'ios') return;

      // Initialize RevenueCat
      try {
        await initPayments();
      } catch (e) {
        console.error('RevenueCat init failed:', e);
      }

      // Auto-restore premium on launch (Apple guideline 3.1.1)
      try {
        const isPremiumUser = await checkIOSPremium();
        if (isPremiumUser) {
          setIsPremium(true);
        }
      } catch (e) {
        console.error('Premium status check failed:', e);
      }

      // Skip info form on iOS - go straight to WelcomeScreen
      const savedEmail = localStorage.getItem('miiq-user-email');
      if (!savedEmail) {
        setHasCollectedInfo(true);
        setScreen('welcome');
      }
    };
    initIOS();
  }, []);
// STATE CHECKER: Handle payment success BEFORE any other initialization
  React.useEffect(() => {
    const initializeSession = async () => {
      // STEP 1: Check payment status FIRST (highest priority)
      const urlParams = new URLSearchParams(window.location.search);
      const paymentSuccess = urlParams.get('payment') === 'success';
      
      if (paymentSuccess) {
        // C1: never trust the URL. Confirm the Stripe session on the server first.
        const sessionId = urlParams.get('session_id');
        const verified = sessionId ? await verifyCheckout(sessionId) : { success: false };
        window.history.replaceState({}, '', window.location.pathname);
        if (!verified.success) {
          alert('We could not confirm your payment yet. If you were charged, please contact support@miiqfitfinder.com and we will unlock your account.');
          return;
        }
        if (verified.email) localStorage.setItem('miiq-user-email', verified.email);

        // Get saved session data
        const savedEmail = localStorage.getItem('miiq-user-email');
        // Try to load completed profile first, then fall back to draft
let saved = null;
try {
  const profileData = localStorage.getItem('miiq-completed-profile');
  saved = profileData ? JSON.parse(profileData) : loadProgress();
} catch (e) {
  saved = loadProgress();
}
        
        if (!saved || !savedEmail) {
          alert('Session data not found. Please restart your assessment.');
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
        
        // Restore COMPLETE session state
        setName(saved.name);
        setRole(saved.role);
        setIndustry(saved.industry);
        setUserPhoto(saved.userPhoto);
        setResponses(saved.responses);
        setQIdx(saved.qIdx);
        setProfile(saved.profile);
        setUserInfo({ email: savedEmail, name: saved.name });
        
        // Set premium flags
        setIsPremium(true);
        setHasCollectedInfo(true);
        
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        
        // Route to unlocked profile
        setScreen('profile');

trackEvent('payment_success', { 
  email: savedEmail, 
  profile: saved.profile,
  industry: saved.industry 
});

// Notify user
        setTimeout(() => {
          alert('? Payment successful! Your progress was saved, and all premium features are now unlocked.');
        }, 500);
        
        return; // EXIT - don't run any other initialization
      }
      
      // STEP 2: Normal session initialization (no payment)
      // Check for ?demo=1 URL parameter — forces fresh info form for reviewers
      const forceDemoFresh = urlParams.get('demo') === '1';
      if (forceDemoFresh) {
        localStorage.removeItem('miiq-user-email');
        return; // skip auto-resume, show info form
      }
      const savedEmail = localStorage.getItem('miiq-user-email');
      // Never auto-resume demo account so it can be re-tested cleanly
      if (savedEmail === 'demo@miiq.app') {
        localStorage.removeItem('miiq-user-email');
        return; // skip auto-resume, show info form
      }
      if (savedEmail) {
        console.log('?? Existing session detected');
        
        // Check premium status from database
        const result = await checkUserPremium(savedEmail);
        
        if (result.success) {
          setUserInfo({ email: savedEmail });
          setHasCollectedInfo(true);
          setScreen("welcome");
          
          if (result.isPremium || savedEmail === 'demo@miiq.app') {
            setIsPremium(true);
          }
        }
      }
    };
    
    initializeSession();
  }, []); // Run once on mount
  
  window.currentIndustry = industry;
  React.useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => {
      setTime(t => {
        if (t <= 1) {
          handleNext();
          return 300;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [running, qIdx]);
  
  // AUTO-SAVE FUNCTIONALITY
  const SAVE_KEY = 'miiq-assessment-draft';
  const PROFILE_KEY = 'miiq-completed-profile';
  const SAVE_EXPIRY_DAYS = 7;
  
  const saveProgress = React.useCallback(() => {
    try {
      const saveData = {
        name,
        role,
        industry,
        userPhoto,
        responses,
        qIdx,
        timestamp: Date.now()
      };
      localStorage.setItem(SAVE_KEY, JSON.stringify(saveData));
    } catch (e) {
      console.log('Save failed:', e);
    }
  }, [name, role, industry, userPhoto, responses, qIdx]);
  
  const loadProgress = React.useCallback(() => {
    try {
  const handleShowPaywall = () => {
  trackEvent('paywall_viewed');
  setShowPaywall(true);
};
      const saved = localStorage.getItem(SAVE_KEY);
      if (!saved) return null;
      const data = JSON.parse(saved);
      const daysOld = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
      if (daysOld > SAVE_EXPIRY_DAYS) {
        localStorage.removeItem(SAVE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }, []);
  
  const clearProgress = React.useCallback(() => {
    localStorage.removeItem(SAVE_KEY);
  }, []);
  
  const hasSavedProgress = React.useCallback(() => {
    return loadProgress() !== null;
  }, [loadProgress]);
  
  // Auto-save when assessment data changes
  React.useEffect(() => {
    if (screen === 'assessment' && qIdx >= 0) {
      saveProgress();
    }
  }, [screen, qIdx, saveProgress]);
  const handleInfoSubmit = async (formData) => {
  trackEvent('info_form_submitted', { email: formData.email });
  setUserInfo(formData);
  setName(formData.name);
  setHasCollectedInfo(true);
  setInfoContext('onboarding');
  localStorage.setItem('miiq-user-email', formData.email);
  // Hardcoded demo bypass for app store testers and reviewers
  if (formData.email === 'demo@miiq.app') {
    setIsPremium(true);
  } else {
    const premiumCheck = await checkUserPremium(formData.email);
    if (premiumCheck.success && premiumCheck.isPremium) {
      setIsPremium(true);
    }
  }
  // If user came from the paywall, return them to it (unless demo unlocked premium); else go to Welcome
  if (pendingPaywall && formData.email !== 'demo@miiq.app') {
    setPendingPaywall(false);
    setShowPaywall(true);
    setScreen('results');
  } else {
    setPendingPaywall(false);
    setShowPaywall(false);
    setScreen('welcome');
  }
  saveUser({
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    industry: industry,
    profileName: null,
    responses: {}
  });
};

const startUnlock = () => {
  trackEvent('paywall_viewed');
  // Apple 5.1.1(v): do NOT require registration before purchase. Go straight to paywall.
  setShowPaywall(true);
};

const handleUpgrade = async () => {
  const platform = Capacitor.getPlatform();

  // Apple 5.1.1(v): no registration required before purchase. Proceed directly to purchase.

  trackEvent('checkout_initiated', { email: userInfo.email });

  if (platform === 'ios') {
    try {
      const result = await handlePurchase(userInfo.email, userInfo.name);
      if (result.success) {
        setIsPremium(true);
        setShowPaywall(false);
        // Server verifies the purchase with RevenueCat, then marks the email premium (cross-device sync)
        if (userInfo.email) {
          const appUserId = await getIOSAppUserId();
          if (appUserId) await syncIOSEntitlement(appUserId, userInfo.email);
        }
      } else if (result.cancelled) {
        // User cancelled - silent, paywall stays open
      } else {
        alert('Purchase failed. Please try again or contact support@miiqfitfinder.com');
      }
    } catch (err) {
      console.error('iOS purchase error:', err);
      alert('Purchase error: ' + (err?.message || 'unknown') + '. Please contact support@miiqfitfinder.com');
    }
  } else {
    // Web/Android - Stripe
    await redirectToPayment(userInfo.email, userInfo.name);
  }
};

const handleRestoreSuccess = async () => {
  setIsPremium(true);
  if (userInfo.email) {
    const appUserId = await getIOSAppUserId();
    if (appUserId) await syncIOSEntitlement(appUserId, userInfo.email);
  }
};  
const onStart = (resuming = false) => {
    if (resuming) {
      const saved = loadProgress();
      if (saved) {
        setName(saved.name);
        setRole(saved.role);
        setIndustry(saved.industry);
        setUserPhoto(saved.userPhoto);
        setResponses(saved.responses);
        setQIdx(saved.qIdx);
        setScreen('assessment');
        setTime(300);
        setRunning(true);
        return;
      }
    }
    // Normal start - default blank name to "Guest" (iOS guest-first; avoids NOT NULL upsert failures)
    if (!name.trim()) setName("Guest");
    setScreen("assessment");
    setQIdx(0);
    setTime(300);
    setRunning(true);
  };
  
  const handleResponseToggle = (key, opt) => {
    setResponses(prev => {
      const current = prev[key] || [];
      const exists = current.some(r => r.value === opt.value);
      return {
        ...prev,
        [key]: exists ? current.filter(r => r.value !== opt.value) : [...current, opt]
      };
    });
  };
  
  const handleNext = () => {
    if (qIdx < QUESTIONS.length - 1) {
      setQIdx(qIdx + 1);
      setTime(300);
    } else {
      const p = determineProfile(responses);
setProfile(p);
// Update Supabase with profile and responses
if (userInfo.email) {
 updateUserProfile(userInfo.email, name, p, responses, industry);
}
setTeam(prev => [...prev, { id: Date.now(), name, role, responses: { ...responses }, profile: p, industry }]);
trackEvent('assessment_completed', { profile: p, industry });
// Save completed profile for payment recovery
try {  localStorage.setItem(PROFILE_KEY, JSON.stringify({
    name,
    role,
    industry,
    userPhoto,
    responses,
    profile: p,
    timestamp: Date.now()
  }));
} catch (e) {
  console.error('Failed to save profile:', e);
}

clearProgress();
setScreen("profile");
      setRunning(false);
    }
  };
  
  const handleBack = () => {
    if (qIdx > 0) setQIdx(qIdx - 1);
  };
  
  const onReset = () => {
    setScreen("welcome");
    setQIdx(0);
    setTime(300);
    setRunning(false);
    setResponses({ experiences: [], growth: [], contribution: [] });
    setCustom("");
    setName("");
    setRole("");
    setProfile(null);
    setShowShare(false);
    setUserPhoto(null);
  };
  
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (!file) {
      setUserPhoto(null);
    }
  };
  
  const onDownload = () => {
    if (profile) downloadResults(name, profile, responses, industry);
  };
  
  const onDownloadPDF = () => {
    if (profile) downloadPDF(name, profile, responses, industry, userPhoto);
  };

  const onDownloadProfilePDF = () => {
    if (profile) downloadProfilePDF(name, profile, industry, userPhoto);
  };
  
  return (
    <>
      {showShare && <ShareModal name={name} profileName={profile} responses={responses} industry={industry} onClose={() => setShowShare(false)} />}
{showPaywall && <PaywallModal onUpgrade={handleUpgrade} onClose={() => setShowPaywall(false)} onRestored={handleRestoreSuccess} />}
{screen === "payment-success" && <PaymentSuccessScreen onViewProfile={() => setScreen('profile')} />}
{screen === "info" && !hasCollectedInfo && <UserInfoForm onSubmit={handleInfoSubmit} context={infoContext} />}
      {screen === "welcome" && hasCollectedInfo && <WelcomeScreen userName={name} userRole={role} onNameChange={setName} onRoleChange={setRole} onStart={onStart} teamCount={team.length} onViewTeam={() => setScreen("team")} industry={industry} onIndustryChange={setIndustry} userPhoto={userPhoto} onPhotoUpload={handlePhotoUpload} hasSavedProgress={hasSavedProgress} clearProgress={clearProgress} onSignOut={() => { localStorage.removeItem('miiq-user-email'); localStorage.removeItem('miiq-assessment-draft'); setName(""); setRole(""); setProfile(null); setResponses({ experiences: [], growth: [], contribution: [] }); setCustom(""); setUserPhoto(null); setQIdx(0); setTime(300); setRunning(false); const _iOS = Capacitor.getPlatform() === 'ios'; setHasCollectedInfo(_iOS); setIsPremium(false); setUserInfo({ name: '', email: '', phone: '' }); setScreen(_iOS ? "welcome" : "info"); setTimeout(() => window.location.reload(), 50); }} onDeleteAccount={handleDeleteAccount} showDeleteConfirm={showDeleteConfirm} setShowDeleteConfirm={setShowDeleteConfirm} deleteInProgress={deleteInProgress} />}
      {screen === "assessment" && <AssessmentScreen question={qIdx} responses={responses} onResponseToggle={handleResponseToggle} custom={custom} onCustomChange={setCustom} onNext={handleNext} onBack={handleBack} time={time} running={running} />}
 {screen === "profile" && <ProfileScreen userName={name} userRole={role} profileName={profile} responses={responses} industry={industry} userPhoto={userPhoto} isPremium={isPremium} onViewResults={() => setScreen("results")} onReset={onReset} onShare={() => setShowShare(true)} onDownload={onDownloadProfilePDF} onShowPaywall={startUnlock} />}
      {screen === "team" && <TeamDashboardScreen teamMembers={team} onAddMember={() => { setName(""); setRole(""); setProfile(null); setResponses({ experiences: [], growth: [], contribution: [] }); setCustom(""); setUserPhoto(null); setQIdx(0); setTime(300); setRunning(false); setHasCollectedInfo(false); setUserInfo({ name: '', email: '', phone: '' }); localStorage.removeItem('miiq-user-email'); setInfoContext('team'); setScreen("info"); }} onBack={() => setScreen("welcome")} onViewMember={(member) => { setName(member.name); setProfile(member.profile); setResponses(member.responses); setIndustry(member.industry); setScreen("profile"); }} />}
      {screen === "results" && <ResultsScreen userName={name} userRole={role} responses={responses} profileName={profile} userPhoto={userPhoto} industry={industry} isPremium={isPremium} onViewProfile={() => setScreen("profile")} onReset={onReset} onShare={() => setShowShare(true)} onDownload={onDownload} onDownloadPDF={onDownloadPDF} onShowPaywall={startUnlock} />}
      {!["info","welcome","assessment","profile","team","results","payment-success"].includes(screen) && (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
            <p className="text-gray-700 mb-4">Something went wrong. Let's get you back home.</p>
            <button onClick={() => setScreen("welcome")} className="px-6 py-3 bg-gradient-to-r from-[#2E1A47] to-[#D4AF37] text-white font-bold rounded-xl">Go to Home</button>
          </div>
        </div>
      )}
    </>
  );
};

export default MiiQFitFinderApp;

















 
